import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { getBebyggelseobjekt } from 'store/bebyggelseobjektSlice';
import { getBebyggelseobjektUtkast } from 'store/bebyggelseobjektUtkastSlice';
import { setMapMode } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';
import { useInitialFocusOnExtent } from 'hooks/useInitialFocusOnExtent';
import { useIsAllowedToEditSkyddsklassning } from 'hooks/useIsAllowedToEditSkyddsklassning';

import AktivitetBaseInfo from 'components/aktivitetPresentation/AktivitetBaseInfo';
import PubliceradeBebyggelseobjekt from 'components/aktivitetPresentation/publiceradeBebyggelseobjekt/PubliceradeBebyggelseobjekt';
import DeleteSkyddsklassningDialog from 'components/aktivitetPresentation/skyddsklassning/deleteSkyddsklassningDialog/DeleteSkyddsklassningDialog';
import PubliceraSkyddsklassningDialog from 'components/aktivitetPresentation/skyddsklassning/publiceraSkyddsklassningDialog/PubliceraSkyddsklassningDialog';
import { DrawerBody, DrawerFooter, DrawerSection } from 'components/drawerSections/DrawerSections';
import {
  replaceBebyggelseobjektFeaturesInActiveSource,
  replaceUtkastFeaturesInActiveSource
} from 'components/map/Layers';

import { BebyggelseobjektUtkastIndex } from 'models';

import {
  AktivitetStatusEnum,
  BebyggelseobjektgruppEnum,
  MapModeEnum,
  SkyddsklassningstypEnum,
  kkmSkyddsklassningstyper
} from 'enums';

import { SkyddsklassningKkmUtkastObjektSection } from './sections/SkyddsklassningKkmUtkastObjektSection';
import { SkyddsklassningSammansattUtkastObjektSection } from './sections/SkyddsklassningSammansattUtkastObjektSection';

const SkyddsklassningPresentation: FC = (): ReactNode => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { setDialog } = useDialog();

  const { selected_organisation, rolesInSelectedOrganisation } = useAppSelector((state: RootState) => state.user);
  const aktivitet = useAppSelector((state: RootState) => state.aktivitet);
  const bebyggelseobjektUtkast = useAppSelector((state: RootState) => state.bebyggelseobjektUtkast);
  const publiceradeBebyggelseobjekt = useAppSelector((state: RootState) => state.publiceradeBebyggelseobjekt);

  const isLoading = aktivitet.requestStatus === 'pending' || bebyggelseobjektUtkast.requestStatus === 'pending';
  const skyddsklassningsAktivitet = aktivitet.data?.skyddsklassning;
  const aktivitetIsAvslutad = skyddsklassningsAktivitet?.aktivitetstatus.status === AktivitetStatusEnum.AVSLUTAD;
  const sammansattUtkast = useMemo<BebyggelseobjektUtkastIndex | undefined>(() => {
    return bebyggelseobjektUtkast.data?.find((x) => x.bebyggelseobjektgrupp === BebyggelseobjektgruppEnum.SAMMANSATT);
  }, [bebyggelseobjektUtkast.data]);
  const sammansattUtkastIsInskickad = sammansattUtkast?.registrering?.registreringsstatustyp_id === 'INSKICKAD';

  const isAllowedToEditSkyddsklassning = useIsAllowedToEditSkyddsklassning(
    skyddsklassningsAktivitet,
    selected_organisation,
    rolesInSelectedOrganisation
  );

  useEffect(() => {
    const isRevidering =
      skyddsklassningsAktivitet?.skyddsklassningstyp_id ===
      SkyddsklassningstypEnum.REVIDERING_AV_STATLIGT_BYGGNADSMINNE;

    if (sammansattUtkast?.utkast_id && isRevidering) {
      dispatch(getBebyggelseobjekt(sammansattUtkast?.utkast_id));
    }
  }, [dispatch, sammansattUtkast?.utkast_id, skyddsklassningsAktivitet?.skyddsklassningstyp_id]);

  useEffect(() => {
    dispatch(setMapMode(MapModeEnum.SAMMANSATT_UTKAST_PRESENTATION));
  }, [dispatch]);

  useEffect(() => {
    if (skyddsklassningsAktivitet && isAllowedToEditSkyddsklassning) {
      dispatch(getBebyggelseobjektUtkast(skyddsklassningsAktivitet.aktivitet_id));
    }
  }, [skyddsklassningsAktivitet, dispatch, isAllowedToEditSkyddsklassning]);

  useEffect(() => {
    replaceBebyggelseobjektFeaturesInActiveSource(publiceradeBebyggelseobjekt.data);
  }, [publiceradeBebyggelseobjekt.data]);

  useInitialFocusOnExtent(
    useCallback(() => replaceUtkastFeaturesInActiveSource(bebyggelseobjektUtkast.data), [bebyggelseobjektUtkast.data])
  );

  return (
    <>
      <DrawerBody>
        {skyddsklassningsAktivitet && <AktivitetBaseInfo aktivitet={skyddsklassningsAktivitet} />}

        <DrawerSection>
          <ul>
            <li>
              <span className="tw-font-semibold">Beslutsorganisation: </span>
              {skyddsklassningsAktivitet?.beslutsorganisation?.organisationsnamn}
            </li>

            {skyddsklassningsAktivitet?.diarienummer && (
              <li>
                <span className="tw-font-semibold">Diarienummer: </span>
                {skyddsklassningsAktivitet.diarienummer}
              </li>
            )}

            {skyddsklassningsAktivitet?.beslutsdatum && (
              <li>
                <span className="tw-font-semibold">Beslutsdatum: </span>
                {skyddsklassningsAktivitet.beslutsdatum}
              </li>
            )}
          </ul>
        </DrawerSection>

        {aktivitetIsAvslutad ? (
          <DrawerSection>
            <h2 className="tw-heading-md tw-mb-2">Berörda objekt ({publiceradeBebyggelseobjekt.data?.length ?? 0})</h2>
            <PubliceradeBebyggelseobjekt
              list={publiceradeBebyggelseobjekt.data}
              error={publiceradeBebyggelseobjekt.error}
              requestStatus={publiceradeBebyggelseobjekt.requestStatus}
            />
          </DrawerSection>
        ) : skyddsklassningsAktivitet &&
          kkmSkyddsklassningstyper.includes(skyddsklassningsAktivitet.skyddsklassningstyp_id) ? (
          <SkyddsklassningKkmUtkastObjektSection />
        ) : (
          <>{isAllowedToEditSkyddsklassning && <SkyddsklassningSammansattUtkastObjektSection />}</>
        )}
      </DrawerBody>

      {isAllowedToEditSkyddsklassning && (
        <DrawerFooter>
          <button
            className="raa-button raa-button-cancel tw-flex-1"
            type="button"
            hidden={aktivitetIsAvslutad || sammansattUtkastIsInskickad}
            disabled={isLoading}
            onClick={() => navigate(`/`, { replace: true })}
          >
            Avbryt
          </button>

          <button
            className="raa-button raa-button-cancel tw-w-full md:tw-flex-1"
            type="button"
            hidden={aktivitetIsAvslutad || sammansattUtkastIsInskickad}
            onClick={() => {
              setDialog(DeleteSkyddsklassningDialog, {
                skyddsklassning: skyddsklassningsAktivitet,
                eTag: aktivitet.data?.eTag
              });
            }}
          >
            Radera
          </button>

          <button
            className="raa-button raa-button-confirm tw-w-full md:tw-flex-1"
            type="button"
            disabled={isLoading || !sammansattUtkast}
            hidden={aktivitetIsAvslutad || sammansattUtkastIsInskickad}
            onClick={() => {
              dispatch(setMapMode(MapModeEnum.SAMMANSATT_UTKAST_PRESENTATION));
              setDialog(PubliceraSkyddsklassningDialog, { id: skyddsklassningsAktivitet?.aktivitet_id });
            }}
          >
            publicera
          </button>
        </DrawerFooter>
      )}
    </>
  );
};

export default SkyddsklassningPresentation;
